import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import {
  PlusIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import axios from "../../../api/axios";
import { ResourceLoading } from "../../../components/utils/ResourceLoading";
import { ResourceError } from "../../../components/utils/ResourceError";
import { ResourceEmpty } from "../../../components/utils/ResourceEmpty";
import DashboardHeader from "../../../components/dashboard/dashboardHeader";

export const DrawingsList = () => {
  const { t } = useTranslation();
  const [drawings, setDrawings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = {
        query: searchQuery,
        per_page: 50,
        page: 1,
      };
      const response = await axios({
        method: "get",
        url: "/drawings/",
        params: params,
      });
      setDrawings(response.data.data || []);
    } catch (err) {
      console.error("Error fetching drawings:", err);
      setError(true);
      toast.error(t("drawings.load_error"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [searchQuery]);

  const handleDelete = async (id) => {
    if (!window.confirm(t("drawings.delete_confirm"))) {
      return;
    }

    try {
      await axios({
        method: "delete",
        url: `/drawings/${id}`,
      });
      toast.success(t("drawings.deleted"));
      fetchData();
    } catch (err) {
      console.error("Error deleting drawing:", err);
      toast.error(t("drawings.delete_error"));
    }
  };

  if (loading) {
    return <ResourceLoading />;
  }

  if (error) {
    return <ResourceError />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <DashboardHeader
        title={t("drawings.list")}
        mainButton={{
          label: t("drawings.new"),
          path: "/dashboard/drawings/new",
          icon: PlusIcon,
        }}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search */}
        <div className="mb-6">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder={t("drawings.search")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Drawings List */}
        {drawings.length === 0 ? (
          <ResourceEmpty message={t("drawings.no_results")} />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {drawings.map((drawing) => (
              <div
                key={drawing.id}
                className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow"
              >
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {drawing.title}
                  </h3>
                  <div className="flex space-x-2">
                    <Link
                      to={`/dashboard/drawings/${drawing.id}`}
                      className="text-blue-600 hover:text-blue-800"
                      title={t("View")}
                    >
                      <EyeIcon className="h-5 w-5" />
                    </Link>
                    <Link
                      to={`/dashboard/drawings/${drawing.id}/edit`}
                      className="text-gray-600 hover:text-gray-800"
                      title={t("Edit")}
                    >
                      <PencilIcon className="h-5 w-5" />
                    </Link>
                    <button
                      onClick={() => handleDelete(drawing.id)}
                      className="text-red-600 hover:text-red-800"
                      title={t("Delete")}
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </div>
                </div>
                {drawing.description && (
                  <p className="text-sm text-gray-600 mb-2">
                    {drawing.description}
                  </p>
                )}
                {drawing.project && (
                  <p className="text-xs text-gray-500">
                    {t("drawings.project")}: {drawing.project.displayTitle}
                  </p>
                )}
                {(drawing.floor || drawing.house) && (
                  <p className="text-xs text-gray-500 mt-1">
                    {drawing.floor && `${t("drawings.floor")}: ${drawing.floor}`}
                    {drawing.floor && drawing.house && " | "}
                    {drawing.house && `${t("drawings.house")}: ${drawing.house}`}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

